/**
 * The map bar — docked at the bottom of every survey screen (D-028).
 *
 * From the design authority, 2026-08-30: *"The surveyor should be able to
 * open navigation from ANY point in the survey, in one gesture, and land
 * anywhere in one tap."* This is the one gesture: a navy bar, always in the
 * thumb zone, that names where you are (the section title as the hero, the
 * survey · room in mono under it — D-037 reversed the order and grew the
 * hero) and how far along the job is (the amber ring), and opens the map on
 * a tap or a swipe up.
 *
 * It is a `<button>`, and it is the ONE navigation control in the app that is
 * not an anchor — deliberately: it navigates nowhere. Opening the sheet is UI
 * state, not a route, so there is no href for it to carry.
 *
 * Presentational: the model comes from `mapTree.ts`; the gesture handling
 * lives in `SurveyMap.tsx`, which owns the sheet's open state.
 */

import type { ReactNode } from 'react';

import type { MapModel } from '../state/mapTree';

export interface MapBarProps {
  model: MapModel;
  onOpen: () => void;
  /** Wired by the owner for the swipe-up gesture. */
  onTouchStart?: (event: React.TouchEvent<HTMLButtonElement>) => void;
  onTouchMove?: (event: React.TouchEvent<HTMLButtonElement>) => void;
}

/**
 * r=24 → 2πr, kept as a literal so the dasharray is one. The ring was the
 * prototype's 46px (r=19, 119.4): its 34px inner disc could not hold "100%"
 * in 16px mono, so the finished survey printed its number over the arc.
 * At 56px the disc is 44px across and the figure sits inside it with air
 * (brand round, 2026-09-06).
 */
export const RING_SIZE = 56;
export const RING_CIRCUMFERENCE = 150.8;

export function ringOffset(pct: number): string {
  const clamped = Math.max(0, Math.min(100, pct));
  return (Math.round(RING_CIRCUMFERENCE * (1 - clamped / 100) * 10) / 10).toFixed(1);
}

export function MapBar({ model, onOpen, onTouchStart, onTouchMove }: MapBarProps): ReactNode {
  const { here, place, room } = model.bar;
  const pct = model.progress.pct;
  return (
    <button
      type="button"
      className="mapbar"
      aria-label={`Open survey map — ${here}, ${String(pct)}% of the survey done`}
      onClick={onOpen}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
    >
      <span className="mapbar__grab" aria-hidden="true" />
      <span className="mapbar__where">
        <span className="mapbar__here" data-legibility="display">
          {here}
        </span>
        <span className="mapbar__crumb" data-legibility="display">
          <span className="mapbar__crumb-place" data-legibility="display">
            {place}
          </span>
          {' · '}
          <span className="mapbar__crumb-room" data-legibility="display">
            {room}
          </span>
        </span>
      </span>
      <span className="mapbar__ring" aria-hidden="true">
        <svg width={RING_SIZE} height={RING_SIZE} viewBox="0 0 56 56">
          <circle className="mapbar__track" cx="28" cy="28" r="24" fill="none" strokeWidth="4" />
          <circle
            className="mapbar__arc"
            cx="28"
            cy="28"
            r="24"
            fill="none"
            strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray={String(RING_CIRCUMFERENCE)}
            strokeDashoffset={ringOffset(pct)}
          />
        </svg>
        <span className="mapbar__pct" data-legibility="meta">
          {pct}%
        </span>
      </span>
    </button>
  );
}
